import React, { useMemo } from "react";
import jwtDecode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { logout } from "../redux/actions/userActions";
import "bootstrap/dist/css/bootstrap.min.css";
import Notifications from "./Notifications";

// Constants
const MOCK_NOTIFICATIONS = [
  {
    UTC: "1408648665",
    list: [
      {
        type: "Message",
        content: "New Sales Order is escalated.",
        count: 3,
        timestamp: "1PM",
      },
    ],
  },
  {
    UTC: "1598103780",
    list: [
      {
        type: "Login",
        content: "New Sales Order is escalated.",
        count: 1,
      },
    ],
  },
  {
    UTC: "1595594400",
    list: [
      {
        type: "Login",
        content: "New Sales Order is escalated.",
        count: 4,
      },
    ],
  },
  {
    UTC: "1595575200",
    list: [
      {
        type: "Critical",
        content: "New Sales Order is escalated.",
        count: 3,
      },
    ],
  },
];

const DEFAULT_MENU = [];

// Utility functions
const decodeToken = (token) => {
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Token decode error:", error);
    return null;
  }
};

const normalizeMenuUrl = (url) => {
  return url?.startsWith("/") ? url : `/${url}`;
};

const parseMenuData = (menuToken) => {
  if (!menuToken) return DEFAULT_MENU;
  
  const decoded = decodeToken(menuToken);
  if (!decoded?.UserMenus) return DEFAULT_MENU;
  
  try {
    return JSON.parse(decoded.UserMenus);
  } catch (error) {
    console.error("Menu parse error:", error);
    return DEFAULT_MENU;
  }
};

// Subcomponents
const NavbarBrand = () => (
  <Link className="navbar-brand" to="/dashboard">
    <img
      src={`${process.env.PUBLIC_URL}/images/trade.png`}
      alt="Logo"
      style={{ width: "85px", height: "60px" }}
    />
  </Link>
);

const NavbarToggler = () => (
  <button
    className="navbar-toggler"
    type="button"
    data-bs-toggle="collapse"
    data-bs-target="#navbarSupportedContent"
    aria-controls="navbarSupportedContent"
    aria-expanded="false"
    aria-label="Toggle navigation"
  >
    <span className="navbar-toggler-icon"></span>
  </button>
);

const DropdownMenu = ({ menu }) => (
  <li className="nav-item dropdown" key={menu.MenuId}>
    <a
      className="nav-link dropdown-toggle"
      href="#"
      id={`dropdown-${menu.MenuId}`}
      role="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      {menu.MenuName}
    </a>
    <ul className="dropdown-menu" aria-labelledby={`dropdown-${menu.MenuId}`}>
      {menu.ChildMenus?.filter(child => child.MenuIsVisible).map(child => (
        <li key={child.MenuId}>
          <Link className="dropdown-item" to={child.MenuURL}>
            {child.MenuName}
          </Link>
        </li>
      ))}
    </ul>
  </li>
);

const SimpleMenuItem = ({ menu }) => (
  <li className="nav-item" key={menu.MenuId}>
    <Link
      className="nav-link active"
      aria-current="page"
      to={normalizeMenuUrl(menu.MenuURL)}
    >
      {menu.MenuName}
    </Link>
  </li>
);

const MenuItem = ({ menu }) => {
  return menu.ChildMenus ? <DropdownMenu menu={menu} /> : <SimpleMenuItem menu={menu} />;
};

const UserMenu = ({ userName, onLogout }) => (
  <div className="btn-group">
    <button
      className="btn btn-info btn-lg"
      style={{ color: "white", width: "200px" }}
      type="button"
    >
      ☰ {userName}
    </button>
    <button
      type="button"
      className="btn btn-info dropdown-toggle dropdown-toggle-split"
      style={{ color: "white" }}
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      <span className="visually-hidden">Toggle Dropdown</span>
    </button>
    <ul className="dropdown-menu">
      <li>
        <Link className="dropdown-item" to="/changepassword">
          Password Change
        </Link>
      </li>
      <li>
        <Link to="/#" className="dropdown-item" onClick={onLogout}>
          Logout
        </Link>
      </li>
    </ul>
  </div>
);

// Main component
const NavigationBar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { userInfo } = useSelector((state) => state.userLogin);

  const userData = useMemo(() => {
    if (!userInfo) return null;
    return decodeToken(userInfo.token);
  }, [userInfo]);

  const menuData = useMemo(() => {
    if (!userInfo) return DEFAULT_MENU;
    const menus = parseMenuData(userInfo.menuToken);
    // Filter out Dashboard menu item
    return menus.filter(menu => menu.MenuName !== "Dashboard");
  }, [userInfo]);

  const handleLogout = () => {
    dispatch(logout());
    history.push("/signin");
  };

  const navbarStyle = {
    backgroundColor: "white",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  };

  return (
    <nav className="navbar navbar-expand-lg sticky-top" style={navbarStyle}>
      <div className="container-fluid">
        <NavbarBrand />
        <NavbarToggler />
        
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            {userInfo && menuData.map(menu => (
              <MenuItem key={menu.MenuId} menu={menu} />
            ))}
            
            <li className="nav-item dropdown">
              <Notifications listItems={MOCK_NOTIFICATIONS} />
            </li>
          </ul>
          
          <div className="d-flex">
            {userInfo && (
              <UserMenu 
                userName={userData?.UserFullName || ""} 
                onLogout={handleLogout}
              />
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavigationBar;